import { INFORMED_OTP_FIELD, PHONE, PHONE_NUMBER } from '../../constants/FieldTypeConstants';
import FormFieldGroup from '../../stores/models/FieldModels/FormFieldGroupField';
import PhoneNumberField from '../../stores/models/FieldModels/PhoneNumberField';
import { AllFormFieldModelsType } from '../../types/FieldModelTypes';
import {
  GetInlineOtpFieldNamesParams,
  PublicFormMachineStateUnion
} from '../../types/MultiStepRegistrationFormTypes';

export const collectStepFields = (
  fields: AllFormFieldModelsType[]
): AllFormFieldModelsType[] =>
  fields.flatMap((field) =>
    field instanceof FormFieldGroup ? collectStepFields(field.formFields) : [field]
  );

export const isPhoneNumberFieldWithOtp = (
  field: AllFormFieldModelsType
): field is PhoneNumberField => {
  const phoneField = field as PhoneNumberField;
  const isPhoneField =
    field instanceof PhoneNumberField ||
    field.type === PHONE ||
    field.type === PHONE_NUMBER;

  return isPhoneField && Boolean(phoneField.shouldShowOtpField);
};

export const getInlineOtpPhoneFieldName = ({
  stepFields,
  formValues,
  publicFormMachineState
}: GetInlineOtpFieldNamesParams): string | undefined => {
  const flattenedStepFields = collectStepFields(stepFields);

  const phoneFieldFromStep = flattenedStepFields.find(
    isPhoneNumberFieldWithOtp
  )?.field;

  if (phoneFieldFromStep) {
    return phoneFieldFromStep;
  }

  const allStepFieldsFromForm =
    'formInfo' in publicFormMachineState.context &&
      publicFormMachineState.context.formInfo?.form?.steps
      ? publicFormMachineState.context.formInfo.form.steps.flatMap(
        (step) => step.stepFields
      )
      : [];

  const phoneFieldFromForm = collectStepFields(allStepFieldsFromForm).find(
    isPhoneNumberFieldWithOtp
  )?.field;

  if (phoneFieldFromForm) {
    return phoneFieldFromForm;
  }

  const phoneFieldFromValues = [
    'phone',
    'phoneNumber',
    'mobile',
    'mobileNumber'
  ].find((fieldName) => Boolean(formValues[fieldName]));

  return phoneFieldFromValues;
};

export const getInlineOtpFieldName = (
  stepFields: AllFormFieldModelsType[]
): string | undefined =>
  collectStepFields(stepFields).find((field) => field.type === INFORMED_OTP_FIELD)
    ?.field;

export const getOtpVerificationErrorField = (
  params: GetInlineOtpFieldNamesParams & {
    isOtpSentToUser?: boolean;
  }
): string | undefined => {
  const { isOtpSentToUser } = params;
  const phoneFieldName = getInlineOtpPhoneFieldName(params);

  if (!isOtpSentToUser) {
    return phoneFieldName;
  }

  return getInlineOtpFieldName(params.stepFields) ?? phoneFieldName;
};

export const isInlineOtpVerified = (
  publicFormMachineState: PublicFormMachineStateUnion,
  stepFields: AllFormFieldModelsType[],
  formValues: Record<string, unknown>
): boolean => {
  const context = publicFormMachineState.context;

  if (context.isLoggedIn || context.isOtpVerified) {
    return true;
  }

  const phoneFieldName = getInlineOtpPhoneFieldName({
    stepFields,
    formValues,
    publicFormMachineState
  });

  const fieldsAuthMachineRefDetails = (
    'fieldsAuthMachineRefDetails' in context
      ? context.fieldsAuthMachineRefDetails
      : undefined
  ) as
    | Record<string, { state: { context: { isOtpVerified?: boolean } } }>
    | undefined;

  if (
    phoneFieldName &&
    fieldsAuthMachineRefDetails?.[phoneFieldName]?.state?.context
      ?.isOtpVerified
  ) {
    return true;
  }

  const authMachineContext = (
    context.authMachineRef as {
      state?: { context?: { isOtpVerified?: boolean } };
    } | null
  )?.state?.context;

  if (authMachineContext?.isOtpVerified) {
    return true;
  }

  return false;
};

export const isInlineOtpVerificationPending = (
  publicFormMachineState: PublicFormMachineStateUnion,
  stepFields: AllFormFieldModelsType[],
  formValues: Record<string, unknown>
): boolean => {
  if (isInlineOtpVerified(publicFormMachineState, stepFields, formValues)) {
    return false;
  }

  if (publicFormMachineState.context.shouldShowGetOTP) {
    return true;
  }

  return Boolean(
    getInlineOtpPhoneFieldName({
      stepFields,
      formValues,
      publicFormMachineState
    })
  );
};
